import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { JsonViewer } from '@/ux-common/src';
import { Input } from '@/ux-common/src/components/ui/input';
import { Separator } from '@radix-ui/react-separator';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';

import { Icons } from '@/components/icons';

const RightPanel: React.FC<{
  selectedNode: any | null;
  onClose: () => void;
  exceptionSearchTerm?: string;
  setExceptionSearchTerm: React.Dispatch<React.SetStateAction<string>>;
}> = ({
  selectedNode,
  onClose,
  exceptionSearchTerm,
  setExceptionSearchTerm,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [totalMatches, setTotalMatches] = useState(0);
  const [matchPaths, setMatchPaths] = useState<string[][]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { messages } = useLanguage();

  useEffect(() => {
    if (exceptionSearchTerm) {
      setSearchTerm(exceptionSearchTerm);
    }
  }, [exceptionSearchTerm]);

  const handleMatchesUpdate = useCallback(
    (total: number, paths: string[][]) => {
      setTotalMatches(total);
      setMatchPaths(paths);
      if (total > 0 && currentMatchIndex === -1) {
        setCurrentMatchIndex(0);
      } else if (total === 0) {
        setCurrentMatchIndex(-1);
      } else if (currentMatchIndex >= total) {
        setCurrentMatchIndex(Math.max(0, total - 1));
      }
    },
    [currentMatchIndex]
  );

  const navigateToMatch = useCallback(
    (direction: 'next' | 'prev') => {
      if (totalMatches === 0) return;

      if (direction === 'next') {
        setCurrentMatchIndex((prev) => (prev + 1) % totalMatches);
      } else {
        setCurrentMatchIndex(
          (prev) => (prev - 1 + totalMatches) % totalMatches
        );
      }
    },
    [totalMatches]
  );

  // Reset current match when search term changes
  useEffect(() => {
    if (searchTerm.trim()) {
      setCurrentMatchIndex(0);
    } else {
      setCurrentMatchIndex(-1);
      setTotalMatches(0);
    }
  }, [searchTerm]);

  const hasMatches = searchTerm.trim() && totalMatches > 0;

  return (
    <div className="relative flex h-full flex-col bg-[#08242F]">
      <div className="pointer-events-none absolute inset-0 h-[42px] bg-[#FFFFFF0F]"></div>

      <div className="flex items-center justify-between overflow-auto px-4 py-2">
        <div className="relative flex h-7 max-w-xs flex-1 items-center gap-1.5 rounded-[2px] border border-[#8C9EA1] px-2 py-3">
          <Search className="h-4 w-4 text-[#C1C9CF]" />
          <Input
            placeholder="Search keyword"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setExceptionSearchTerm(e.target.value);
            }}
            className="h-[18px] flex-1 border-0 border-none p-0 text-xs text-form-input-disabled text-slate-200 placeholder-slate-400 focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
          />
          {hasMatches && (
            <div className="ml-2 flex items-center gap-1">
              <span className="text-xs text-slate-400">
                {currentMatchIndex + 1}/{totalMatches}
              </span>
              <button
                onClick={() => navigateToMatch('prev')}
                className="rounded p-0.5 transition-colors hover:bg-slate-700"
                disabled={totalMatches === 0}
              >
                <ChevronUp className="h-3 w-3 text-slate-400" />
              </button>
              <button
                onClick={() => navigateToMatch('next')}
                className="rounded p-0.5 transition-colors hover:bg-slate-700"
                disabled={totalMatches === 0}
              >
                <ChevronDown className="h-3 w-3 text-slate-400" />
              </button>
            </div>
          )}
        </div>
        <div className="ml-2 flex items-center justify-between">
          {/* <a
            href={`vscode://file${selectedNode?.attributes.span_source}`}
            className="border-none outline-none"
          > */}
          <div className="flex cursor-default items-center gap-2 p-0.5 pl-2 pr-3">
            <span className="text-body-sm text-form-input-disabled">
              Open source path
            </span>
            {Icons.PopoutIcon()}
            <span className="ml-1 rounded bg-[#EFDA1F] px-1.5 text-btn-label-sm text-[#07181E] opacity-[48%]">
              {messages?.General?.wip}
            </span>
          </div>
          {/* </a> */}
          <Separator
            orientation="vertical"
            className="mr-2 h-5 w-[1px] bg-[#0A343C]"
          />
          <div className="flex h-6 w-6 items-center justify-center border-none outline-none">
            <button
              onClick={onClose}
              className="rounded p-1 transition-colors hover:bg-slate-700"
            >
              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className="flex-1 space-y-1 overflow-auto px-4 py-2"
      >
        <JsonViewer
          data={selectedNode}
          searchTerm={searchTerm}
          currentMatchIndex={currentMatchIndex}
          onMatchesUpdate={handleMatchesUpdate}
          scrollContainer={scrollContainerRef}
          matchPaths={matchPaths}
        />
      </div>
    </div>
  );
};
export default RightPanel;
